import { Row, Col } from 'react-bootstrap';
import { useGetLumuIncidentsMutation, useGetSitesQuery, useGetAirwaveInfoQuery, useGetGraylogErrorsQuery, useDattoAuthMutation } from '../slices/sitesApiSlice';
import { useGetDattoOpenAlertsQuery } from '../slices/dattoRmmApiSlice';
import { useGetQuarantinedEmailsQuery } from '../slices/proofpointApiSlice';
import { useGetUnitrendsJobsQuery } from '../slices/unitrendsApiSlice';
import { useGetTasksQuery } from '../slices/tasksApiSlice';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import * as L from "leaflet";
import lumuLogo from '../assets/lumu_logo.png';
import arubaLogo from '../assets/aruba_logo.png';
import graylogLogo from '../assets/graylog.png';
import yellow from '../assets/marker-icon-yellow.png';
import green from '../assets/marker-icon-green.png';
import red from '../assets/marker-icon-red.png';
import blue from '../assets/marker-icon-blue.png';
import { handleUnauthorized } from '../utils/auth';

const HomeScreen = () => {
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);

  const { data: sites, isLoading, error } = useGetSitesQuery(undefined, {
    skip: !userInfo
  });

  useEffect(() => {
    if (error) {
      handleUnauthorized(error);
    }
  }, [error]);
  
  useEffect(() => {
    if (!userInfo) {
      navigate('/login');
    }
  }, [userInfo, navigate]);

  const [getLumuIncidents] = useGetLumuIncidentsMutation();
  const [dattoAuth] = useDattoAuthMutation();
  const { data: airwaveInformation } = useGetAirwaveInfoQuery(undefined, {
    skip: !userInfo
  });
  const { data: graylogInfo } = useGetGraylogErrorsQuery(undefined, {
    skip: !userInfo
  });
  const { data: dattoAlerts, isLoading: alertsLoading, error: alertsError } = useGetDattoOpenAlertsQuery(undefined, {
    skip: !userInfo
  });
  const { data: quarantinedEmails, isLoading: proofpointLoading, error: proofpointError } = useGetQuarantinedEmailsQuery(undefined, {
    skip: !userInfo
  });
  const { data: unitrendsJobs, isLoading: unitrendsLoading, error: unitrendsError } = useGetUnitrendsJobsQuery(undefined, {
    skip: !userInfo
  });
  const { data: tasks } = useGetTasksQuery(undefined, {
    skip: !userInfo
  });

  const dattoUidToSiteIdMap = {};
  if (sites) {
    sites.forEach(site => {
      if (site.DattoUID) {
        dattoUidToSiteIdMap[site.DattoUID] = site._id;
      }
    });
  }

  const getPriorityClass = (priority) => {
    if (!priority) return 'default';
    const p = priority.toLowerCase();
    if (p === 'critical') return 'critical';
    if (p === 'high') return 'high';
    if (p === 'moderate' || p === 'medium') return 'moderate';
    if (p === 'low') return 'low';
    return 'default';
  };

  const getAlertDetails = (alert) => {
    if (!alert) return 'No details provided';
    
    const context = alert.alertContext || {};
    const klass = context['@class'];
    
    // 1. Event Log Alerts
    if (klass === 'eventlog_ctx') {
      return context.description || `Event Log Error (${context.source || 'Unknown Source'}, Code: ${context.code || 'N/A'})`;
    }
    
    // 2. Service Status Alerts
    if (klass === 'srvc_status_ctx') {
      return `Service "${context.serviceName || 'Unknown'}" is ${context.status || 'STOPPED'}`;
    }
    
    // 3. Performance Disk Usage Alerts
    if (klass === 'perf_disk_usage_ctx') {
      const freeGB = context.freeSpace ? (context.freeSpace / 1024 / 1024).toFixed(2) : null;
      const totalGB = context.totalVolume ? (context.totalVolume / 1024 / 1024).toFixed(2) : null;
      if (freeGB && totalGB) {
        return `Disk ${context.diskName || ''} space low: ${freeGB} GB free of ${totalGB} GB`;
      }
      return `Disk ${context.diskName || ''} free space is low (${context.freeSpace || 'N/A'} free)`;
    }
    
    // 4. Endpoint Security Threat Alerts
    if (klass === 'endpoint_security_threat_ctx') {
      return context.description || 'Detected endpoint security threat';
    }
    
    // Fallback to top-level diagnostics or context description
    return alert.diagnostics || context.description || 'No details provided';
  };

  const [graylogErrors, setGraylogErrors] = useState([{
    server: "",
    count: 0
  }]);
  const [lumuIncidents, setLumuIncidents] = useState({
    new: 0,
    inProgress: 0
  });
  const [airwaveInfo, setAirwaveInfo] = useState({
    alerts: 0,
    up: 0,
    down: 0,
    clients: 0
  });

  const LeafIcon = L.Icon.extend({
    options: {}
  });

  const yellowIcon = new LeafIcon({ iconUrl: yellow });
  const redIcon = new LeafIcon({ iconUrl: red });
  const greenIcon = new LeafIcon({ iconUrl: green });
  const blueIcon = new LeafIcon({ iconUrl: blue });

  const getTaskCountForSite = (siteId) => {
    if (!tasks) return 0;
    return tasks.filter(t => t.site?._id === siteId || t.site === siteId).length;
  };

  const getMarkerIcon = (site) => {
    let iconUrl = blue;
    if (site.company === "Catholic Charities") iconUrl = red;
    else if (site.company === "Cemeteries") iconUrl = yellow;
    else if (site.company === "Diocese of Camden") iconUrl = blue;
    else if (site.company === "Housing") iconUrl = green;

    const taskCount = getTaskCountForSite(site._id);

    if (taskCount > 0) {
      return L.divIcon({
        className: 'custom-div-icon',
        html: `
          <div style="position: relative; display: inline-block;">
            <img src="${iconUrl}" style="width: 25px; height: 41px;" />
            <div style="position: absolute; top: -8px; right: -8px; background-color: #ff4757; color: white; border-radius: 50%; width: 22px; height: 22px; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: bold; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3); z-index: 1000;">
              ${taskCount}
            </div>
          </div>
        `,
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
      });
    }

    return new LeafIcon({ iconUrl: iconUrl });
  };

  useEffect(() => {
    if (userInfo) {
      let open_new = 0;
      let open_progress = 0;
      dattoAuth().then(() => {});
      getLumuIncidents().then(res => {
        if (res?.data?.items) {
          res.data.items.forEach((item) => {
            if (item.unread) {
              open_new++;
            } else {
              open_progress++;
            }
          });
          setLumuIncidents({
            new: open_new,
            inProgress: open_progress
          });
        }
      });
    }
  }, [userInfo, dattoAuth, getLumuIncidents]);

  useEffect(() => {
    if (airwaveInformation && userInfo) {
      setAirwaveInfo(airwaveInformation);
    }
  }, [airwaveInformation, userInfo]);

  useEffect(() => {
    if (graylogInfo && userInfo) {
      setGraylogErrors(graylogInfo);
    }
  }, [graylogInfo, userInfo]);

  if (!userInfo) {
    return null;
  }

  return (
    <>
      <style>{`
        .modern-dashboard {
          color: #ffffff;
          font-family: 'Outfit', 'Inter', sans-serif;
          max-width: 1300px;
          margin: 0 auto;
          padding: 20px;
        }

        .dashboard-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          margin-bottom: 25px;
        }

        .dashboard-tag {
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 2px;
          color: #00cec9;
          text-transform: uppercase;
          margin-bottom: 4px;
        }

        .dashboard-title {
          font-size: 32px;
          font-weight: 700;
          background: var(--theme-gradient-light);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin: 0;
        }

        .dashboard-subtitle {
          font-size: 14px;
          color: #b2bec3;
          margin: 5px 0 0 0;
        }

        .glass-card {
          background: rgba(30, 39, 46, 0.6);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 16px;
          padding: 24px;
          box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.4);
          transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
          overflow: hidden;
          position: relative;
        }

        .glass-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 35px 0 rgba(0, 0, 0, 0.6);
          border-color: rgba(255, 255, 255, 0.15);
        }

        .map-card {
          margin-bottom: 30px;
          padding: 12px;
          background: rgba(30, 39, 46, 0.4);
        }

        .dark-map {
          border-radius: 12px;
          overflow: hidden;
          border: 1px solid rgba(255, 255, 255, 0.05);
        }

        .dark-map .leaflet-container {
          filter: invert(90%) hue-rotate(200deg) brightness(85%) contrast(105%);
        }

        .card-header-flex {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.08);
          padding-bottom: 12px;
        }

        .card-title {
          font-size: 18px;
          font-weight: 600;
          color: #ffffff;
          margin: 0;
          letter-spacing: 0.5px;
        }

        .card-logo {
          height: 24px;
          object-fit: contain;
          opacity: 0.85;
          transition: opacity 0.2s ease;
        }

        .glass-card:hover .card-logo {
          opacity: 1;
        }

        .card-accent-lumu::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #ff7675, #d63031);
        }

        .card-accent-aruba::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #ffeaa7, #e17055);
        }

        .card-accent-graylog::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #81ecec, #0984e3);
        }

        .metric-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 15px;
          margin-bottom: 10px;
        }

        .metric-box {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 12px;
          padding: 14px 10px;
          text-align: center;
          transition: background 0.2s ease;
        }

        .metric-box:hover {
          background: rgba(255, 255, 255, 0.06);
        }

        .metric-label {
          font-size: 11px;
          color: #a0aec0;
          margin-bottom: 5px;
          text-transform: uppercase;
          font-weight: 700;
          letter-spacing: 0.5px;
        }

        .metric-value {
          font-size: 26px;
          font-weight: 700;
          margin: 0;
        }

        .text-success-glow {
          color: #2ed573;
          text-shadow: 0 0 12px rgba(46, 213, 115, 0.35);
        }

        .text-danger-glow {
          color: #ff4757;
          text-shadow: 0 0 12px rgba(255, 71, 87, 0.35);
        }

        .text-warning-glow {
          color: #ffa502;
          text-shadow: 0 0 12px rgba(255, 165, 2, 0.35);
        }

        .text-info-glow {
          color: #70a1ff;
          text-shadow: 0 0 12px rgba(112, 161, 255, 0.35);
        }

        .text-muted-glow {
          color: #dfe6e9;
        }

        .portal-button {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          padding: 11px;
          border-radius: 10px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.08);
          color: #dfe6e9;
          font-size: 13px;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.2s ease;
          margin-top: 15px;
          letter-spacing: 0.3px;
        }

        .portal-button:hover {
          background: rgba(255, 255, 255, 0.12);
          border-color: rgba(255, 255, 255, 0.2);
          color: #ffffff;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }

        .server-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
          max-height: 180px;
          overflow-y: auto;
          padding-right: 4px;
        }

        .server-list::-webkit-scrollbar {
          width: 4px;
        }

        .server-list::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 2px;
        }

        .server-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 14px;
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.04);
          border-radius: 10px;
          transition: background 0.2s ease;
        }

        .server-item:hover {
          background: rgba(255, 255, 255, 0.04);
        }

        .server-name {
          font-size: 13px;
          color: #e2e8f0;
          font-family: 'Courier New', Courier, monospace;
          font-weight: 600;
        }

        .error-pill {
          font-size: 11px;
          font-weight: 700;
          padding: 3px 10px;
          border-radius: 20px;
          background: rgba(255, 71, 87, 0.15);
          color: #ff4757;
          border: 1px solid rgba(255, 71, 87, 0.2);
        }

        .error-pill.zero {
          background: rgba(46, 213, 115, 0.15);
          color: #2ed573;
          border: 1px solid rgba(46, 213, 115, 0.2);
        }

        .boldText {
          font-weight: 600;
        }

        .homePopup {
          font-family: sans-serif;
        }

        .homePopup ul {
          list-style: none;
          padding: 5px 0;
          margin: 0;
          color: #333;
        }

        .homePopup li {
          margin-bottom: 6px;
        }

        .homePopup a {
          display: inline-block;
          margin-top: 10px;
          padding: 6px 14px;
          background: #74b9ff;
          color: #fff !important;
          border-radius: 6px;
          font-weight: 600;
          text-decoration: none;
          font-size: 12px;
        }

        .homePopup a:hover {
          background: var(--btn-primary-bg);
        }

        .alerts-section {
          margin-top: 30px;
        }

        .alerts-table-container {
          max-height: 400px;
          overflow-y: auto;
          margin-top: 15px;
        }

        .alerts-table-container::-webkit-scrollbar {
          width: 6px;
        }

        .alerts-table-container::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 3px;
        }

        .alerts-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13.5px;
        }

        .alerts-table th {
          position: sticky;
          top: 0;
          background: rgba(30, 39, 46, 0.95);
          color: #a0aec0;
          font-weight: 700;
          text-transform: uppercase;
          font-size: 11px;
          letter-spacing: 0.5px;
          padding: 12px 16px;
          border-bottom: 2px solid rgba(255, 255, 255, 0.08);
          z-index: 1;
        }

        .alerts-table td {
          padding: 12px 16px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
          color: #e2e8f0;
          vertical-align: middle;
        }

        .alerts-table tr:hover {
          background: rgba(255, 255, 255, 0.02);
        }

        .alerts-table tr:last-child td {
          border-bottom: none;
        }

        .alert-badge {
          display: inline-block;
          padding: 4px 10px;
          font-size: 11px;
          font-weight: 700;
          border-radius: 12px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          text-align: center;
          min-width: 85px;
        }

        .alert-badge.critical {
          background: rgba(255, 71, 87, 0.15);
          color: #ff4757;
          border: 1px solid rgba(255, 71, 87, 0.3);
        }

        .alert-badge.high {
          background: rgba(255, 127, 80, 0.15);
          color: #ff7f50;
          border: 1px solid rgba(255, 127, 80, 0.3);
        }

        .alert-badge.moderate {
          background: rgba(255, 165, 0, 0.15);
          color: #ffa500;
          border: 1px solid rgba(255, 165, 0, 0.3);
        }

        .alert-badge.low {
          background: rgba(46, 213, 115, 0.15);
          color: #2ed573;
          border: 1px solid rgba(46, 213, 115, 0.3);
        }

        .alert-badge.default {
          background: rgba(112, 161, 255, 0.15);
          color: #70a1ff;
          border: 1px solid rgba(112, 161, 255, 0.3);
        }

        .alert-site-link {
          color: #00cec9;
          font-weight: 600;
          text-decoration: none;
          transition: color 0.2s ease;
        }

        .alert-site-link:hover {
          color: #81ecec;
          text-decoration: underline;
        }

        .alert-device-name {
          font-family: 'Courier New', Courier, monospace;
          font-weight: 600;
          color: #e2e8f0;
        }

        .card-accent-datto::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: var(--theme-gradient-horizontal);
        }

        .card-accent-proofpoint::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #0984e3, #74b9ff);
        }

        .card-accent-unitrends::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #00b894, #55efc4);
        }

        .card-accent-tasks::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #6c5ce7, #a29bfe);
        }
      `}</style>

      <div className="modern-dashboard">
        {isLoading ? (
          <div className="text-center py-5">
            <h2 className="text-white">Loading Dashboard...</h2>
          </div>
        ) : error ? (
          <div className="alert alert-danger">
            {error?.data?.message || error.error || "An error occurred."}
          </div>
        ) : (
          <>
            {/* Dashboard Header */}
            <div className="dashboard-header">
              <div>
                <div className="dashboard-tag">DOC Operations Center</div>
                
               
              </div>
              <div className="text-end d-none d-md-block">
                <span className="badge bg-success px-3 py-2" style={{ borderRadius: '8px' }}>
                  {sites?.length || 0} Active Sites
                </span>
              </div>
            </div>

            {/* Map Section */}
            <div className="glass-card map-card">
              <div className="dark-map">
                <MapContainer
                  center={["39.6416467", "-74.7987457"]}
                  zoom={8}
                  scrollWheelZoom={true}
                  style={{ width: "100%", height: "calc(55vh - 2rem)" }}
                >
                  <TileLayer
                    url="http://{s}.tile.osm.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                  />
                  {sites.map((site) => (
                    <Marker key={site._id} position={[site.lat, site.lng]} icon={getMarkerIcon(site)}>
                      <Popup className="homePopup">
                        <ul>
                          <li><span className="boldText">Site:</span> {site.name} ({site.abbr})</li>
                          <li><span className="boldText">Company:</span> {site.company}</li>
                          <li><span className="boldText">Address:</span> {site.locationAddress.address}</li>
                          <li>{site.locationAddress.city}, {site.locationAddress.state} {site.locationAddress.postalCode}</li>
                        </ul>
                        <div className="text-center">
                          <a href={`/sites/${site._id}`}>View Details</a>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>
            </div>

            {/* Metrics Integrations Grid */}
            <Row className="g-4">
              {/* Lumu Security Card */}
              <Col lg={4} md={6}>
                <div className="glass-card card-accent-lumu h-100 d-flex flex-column justify-content-between">
                  <div>
                    <div className="card-header-flex">
                      <h4 className="card-title">Lumu Compromises</h4>
                      <img src={lumuLogo} alt="Lumu" className="card-logo" />
                    </div>

                    <div className="metric-grid">
                      <div className="metric-box">
                        <div className="metric-label">New Alerts</div>
                        <div className={`metric-value ${lumuIncidents.new > 0 ? 'text-danger-glow' : 'text-success-glow'}`}>
                          {lumuIncidents.new}
                        </div>
                      </div>
                      <div className="metric-box">
                        <div className="metric-label">In Progress</div>
                        <div className="metric-value text-warning-glow">
                          {lumuIncidents.inProgress}
                        </div>
                      </div>
                    </div>
                  </div>

                  <a 
                    className="portal-button" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    href="https://portal.lumu.io/compromise/incidents"
                  >
                    Open Lumu Portal &nbsp;↗
                  </a>
                </div>
              </Col>

              {/* Aruba Wireless Card */}
              <Col lg={4} md={6}>
                <div className="glass-card card-accent-aruba h-100 d-flex flex-column justify-content-between">
                  <div>
                    <div className="card-header-flex">
                      <h4 className="card-title">Aruba Airwave</h4>
                      <img src={arubaLogo} alt="Aruba" className="card-logo" />
                    </div>

                    <div className="metric-grid">
                      <div className="metric-box">
                        <div className="metric-label">APs Online</div>
                        <div className="metric-value text-success-glow">
                          {airwaveInfo.up}
                        </div>
                      </div>
                      <div className="metric-box">
                        <div className="metric-label">APs Offline</div>
                        <div className={`metric-value ${airwaveInfo.down > 0 ? 'text-danger-glow' : 'text-success-glow'}`}>
                          {airwaveInfo.down}
                        </div>
                      </div>
                      <div className="metric-box">
                        <div className="metric-label">Alerts</div>
                        <div className={`metric-value ${airwaveInfo.alerts > 0 ? 'text-warning-glow' : 'text-success-glow'}`}>
                          {airwaveInfo.alerts}
                        </div>
                      </div>
                      <div className="metric-box">
                        <div className="metric-label">Wifi Clients</div>
                        <div className="metric-value text-info-glow">
                          {airwaveInfo.clients}
                        </div>
                      </div>
                    </div>
                  </div>

                  <a 
                    className="portal-button" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    href="https://hsc-va02.vhs1.org/root"
                  >
                    Open Airwave Portal &nbsp;↗
                  </a>
                </div>
              </Col>

              {/* Graylog Diagnostics Card */}
              <Col lg={4} md={12}>
                <div className="glass-card card-accent-graylog h-100 d-flex flex-column justify-content-between">
                  <div>
                    <div className="card-header-flex">
                      <h4 className="card-title">Graylog Server Diagnostics</h4>
                      <img src={graylogLogo} alt="Graylog" className="card-logo" />
                    </div>

                    <div className="server-list">
                      {graylogErrors.length > 0 && graylogErrors[0].server ? (
                        graylogErrors.map((error, idx) => (
                          <div key={idx} className="server-item">
                            <span className="server-name">{error.server}</span>
                            <span className={`error-pill ${error.count === 0 ? 'zero' : ''}`}>
                              {error.count} Errors
                            </span>
                          </div>
                        ))
                      ) : (
                        <div className="text-center text-muted py-3">
                          No server telemetry available.
                        </div>
                      )}
                    </div>
                  </div>

                  <a 
                    className="portal-button" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    href="http://172.30.1.52:9000"
                  >
                    Open Graylog Diagnostics &nbsp;↗
                  </a>
                </div>
              </Col>
            </Row>

            {/* Active Tasks Section */}
            <div className="glass-card card-accent-tasks alerts-section">
              <div className="card-header-flex">
                <h4 className="card-title">Active Site Tasks</h4>
                <div>
                  <span className="badge bg-info px-3 py-2" style={{ borderRadius: '8px', color: '#fff' }}>
                    {tasks?.length || 0} Tasks
                  </span>
                </div>
              </div>

              {tasks === undefined ? (
                <div className="text-center py-4">
                  <span className="text-muted">Loading tasks...</span>
                </div>
              ) : !tasks || tasks.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  No active tasks.
                </div>
              ) : (
                <div className="alerts-table-container">
                  <table className="alerts-table">
                    <thead>
                      <tr>
                        <th style={{ width: '10%' }}>Priority</th>
                        <th style={{ width: '20%' }}>Site</th>
                        <th style={{ width: '35%' }}>Task</th>
                        <th style={{ width: '15%' }}>Assigned To</th>
                        <th style={{ width: '20%' }}>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tasks.map((task, idx) => {
                         let badgeClass = 'default';
                         const priority = (task.priority || '').toLowerCase();
                         if (priority === 'high' || priority === 'critical') badgeClass = 'critical';
                         else if (priority === 'medium' || priority === 'moderate') badgeClass = 'moderate';
                         else if (priority === 'low') badgeClass = 'low';

                         return (
                           <tr key={task._id || idx}>
                             <td>
                               <span className={`alert-badge ${badgeClass}`}>
                                 {task.priority || 'N/A'}
                               </span>
                             </td>
                             <td>
                               {task.site ? (
                                 <Link to={`/sites/${task.site._id}`} className="alert-site-link">
                                   {task.site.name || task.site.abbr || 'Unknown Site'}
                                 </Link>
                               ) : (
                                 <span className="text-muted">Unknown Site</span>
                               )}
                             </td>
                             <td>
                               <div className="text-white" style={{ whiteSpace: 'pre-wrap' }}>{task.content || 'No details'}</div>
                             </td>
                             <td>
                               <div className="text-white">{task.assigned_to || 'Unassigned'}</div>
                             </td>
                             <td>
                               <span className="text-muted">
                                 {task.createdAt ? new Date(task.createdAt).toLocaleString() : 'N/A'}
                               </span>
                             </td>
                           </tr>
                         );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Datto RMM Open Alerts Section */}
            <div className="glass-card card-accent-datto alerts-section">
              <div className="card-header-flex">
                <h4 className="card-title">Datto RMM Open Alerts</h4>
                <div>
                  <span className="badge bg-danger px-3 py-2" style={{ borderRadius: '8px' }}>
                    {dattoAlerts ? dattoAlerts.length : 0} Open Alerts
                  </span>
                </div>
              </div>

              {alertsLoading ? (
                <div className="text-center py-4">
                  <span className="text-muted">Loading alerts...</span>
                </div>
              ) : alertsError ? (
                <div className="text-center py-4 text-danger">
                  Error loading Datto RMM alerts: {alertsError?.data?.message || alertsError.error || "Connection timed out."}
                </div>
              ) : !dattoAlerts || dattoAlerts.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  No open alerts reported.
                </div>
              ) : (
                <div className="alerts-table-container">
                  <table className="alerts-table">
                    <thead>
                      <tr>
                        <th style={{ width: '10%' }}>Priority</th>
                        <th style={{ width: '15%' }}>Site</th>
                        <th style={{ width: '25%' }}>Device</th>
                        <th style={{ width: '35%' }}>Details</th>
                        <th style={{ width: '15%' }}>Triggered</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dattoAlerts.map((alert) => {
                        const siteId = dattoUidToSiteIdMap[alert.alertSourceInfo?.siteUid];
                        return (
                          <tr key={alert.alertUid}>
                            <td>
                              <span className={`alert-badge ${getPriorityClass(alert.priority)}`}>
                                {alert.priority || 'Unknown'}
                              </span>
                            </td>
                            <td>
                              {siteId ? (
                                <Link to={`/sites/${siteId}`} className="alert-site-link">
                                  {alert.alertSourceInfo?.siteName || 'Unknown Site'}
                                </Link>
                              ) : (
                                <span className="text-muted">{alert.alertSourceInfo?.siteName || 'Unknown Site'}</span>
                              )}
                            </td>
                            <td>
                              <span className="alert-device-name">
                                {alert.alertSourceInfo?.deviceName || 'N/A'}
                              </span>
                            </td>
                            <td>
                              <span className="text-white">{getAlertDetails(alert)}</span>
                            </td>
                            <td>
                              <span className="text-muted">
                                {alert.timestamp ? new Date(alert.timestamp).toLocaleString() : 'N/A'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Proofpoint Quarantined Emails Section */}
            <div className="glass-card card-accent-proofpoint alerts-section">
              <div className="card-header-flex">
                <h4 className="card-title">Proofpoint Quarantined Emails (Last Hour)</h4>
                <div>
                  <span className="badge bg-warning px-3 py-2" style={{ borderRadius: '8px', color: '#000' }}>
                    {quarantinedEmails?.length || 0} Quarantined
                  </span>
                </div>
              </div>

              {proofpointLoading ? (
                <div className="text-center py-4">
                  <span className="text-muted">Loading Proofpoint data...</span>
                </div>
              ) : proofpointError ? (
                <div className="text-center py-4 text-danger">
                  Error loading Proofpoint data: {proofpointError?.data?.message || proofpointError.error || "Connection timed out."}
                </div>
              ) : !quarantinedEmails || quarantinedEmails.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  No quarantined emails found in the last hour.
                </div>
              ) : (
                <div className="alerts-table-container">
                  <table className="alerts-table">
                    <thead>
                      <tr>
                        <th style={{ width: '20%' }}>Recipient</th>
                        <th style={{ width: '20%' }}>Sender</th>
                        <th style={{ width: '40%' }}>Subject</th>
                        <th style={{ width: '20%' }}>Timestamp</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quarantinedEmails.map((email, idx) => (
                        <tr key={email.id || idx}>
                          <td>
                            <span className="alert-device-name">
                              {email.recipient || 'N/A'}
                            </span>
                          </td>
                          <td>
                            <span className="text-white">{email.sender || 'N/A'}</span>
                          </td>
                          <td>
                            <span className="text-muted">{email.subject || 'N/A'}</span>
                          </td>
                          <td>
                            <span className="text-muted">
                              {email.timestamp ? new Date(email.timestamp).toLocaleString() : 'N/A'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Unitrends Jobs Section */}
            <div className="glass-card card-accent-unitrends alerts-section">
              <div className="card-header-flex">
                <h4 className="card-title">Unitrends Backup Jobs (Last 48 Hours)</h4>
                <div>
                  <span className="badge bg-primary px-3 py-2" style={{ borderRadius: '8px' }}>
                    {unitrendsJobs?.length || 0} Jobs
                  </span>
                </div>
              </div>

              {unitrendsLoading ? (
                <div className="text-center py-4">
                  <span className="text-muted">Loading Unitrends data...</span>
                </div>
              ) : unitrendsError ? (
                <div className="text-center py-4 text-danger">
                  Error loading Unitrends data: {unitrendsError?.data?.message || unitrendsError.error || "Connection timed out."}
                </div>
              ) : !unitrendsJobs || unitrendsJobs.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  No backup jobs found in the last 48 hours.
                </div>
              ) : (
                <div className="alerts-table-container">
                  <table className="alerts-table">
                    <thead>
                      <tr>
                        <th style={{ width: '15%' }}>Status</th>
                        <th style={{ width: '25%' }}>Job / Type</th>
                        <th style={{ width: '20%' }}>Client / System</th>
                        <th style={{ width: '25%' }}>Start Date</th>
                        <th style={{ width: '15%' }}>Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unitrendsJobs.map((job, idx) => {
                         let badgeClass = 'default';
                         const status = (job.status || '').toLowerCase();
                         if (status === 'failed' || status === 'error' || job.failures > 0) badgeClass = 'critical';
                         else if (status === 'running') badgeClass = 'moderate';
                         else if (status === 'success' || status === 'successful') badgeClass = 'low';

                         return (
                           <tr key={job.id || idx}>
                             <td>
                               <span className={`alert-badge ${badgeClass}`}>
                                 {job.status || (job.failures > 0 ? 'Failed' : 'Success')}
                               </span>
                             </td>
                             <td>
                               <div className="alert-device-name">{job.name || job.description || 'Unknown Job'}</div>
                               <div className="text-muted" style={{ fontSize: '11px' }}>{job.type || job.app_name || 'N/A'} {job.mode ? `(${job.mode})` : ''}</div>
                             </td>
                             <td>
                               <div className="text-white">{job.client_name || 'N/A'}</div>
                               <div className="text-muted" style={{ fontSize: '11px' }}>{job.system_name || ''}</div>
                             </td>
                             <td>
                               <span className="text-muted">
                                 {job.start_date || job.start_time ? new Date(job.start_date || job.start_time).toLocaleString() : 'N/A'}
                               </span>
                             </td>
                             <td>
                               <span className="text-muted">{job.duration || 'N/A'}</span>
                             </td>
                           </tr>
                         );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          </>
        )}
      </div>
    </>
  );
};

export default HomeScreen;
